import React, { useState, useEffect, useRef, useCallback } from "react";
import { useUnitStore, LengthUnit, ForceUnit } from "../../store/useUnitStore";

interface UnitConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UnitConfigDialog: React.FC<UnitConfigDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { lengthUnit, forceUnit, setLengthUnit, setForceUnit } = useUnitStore();

  const [selectedLengthUnit, setSelectedLengthUnit] =
    useState<LengthUnit>(lengthUnit);
  const [selectedForceUnit, setSelectedForceUnit] =
    useState<ForceUnit>(forceUnit);

  const [showConfirmation, setShowConfirmation] = useState(false);

  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Initialize selected units when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedLengthUnit(lengthUnit);
      setSelectedForceUnit(forceUnit);
    }
  }, [isOpen, lengthUnit, forceUnit]);

  // Center dialog on open
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const dialogWidth = 550;
      const dialogHeight = dialogRef.current.offsetHeight || 400;
      setPosition({
        x: Math.max(0, (window.innerWidth - dialogWidth) / 2),
        y: Math.max(0, (window.innerHeight - dialogHeight) / 2),
      });
    }
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      headerRef.current &&
      headerRef.current.contains(target) &&
      !target.closest("button")
    ) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      document.body.style.userSelect = "none";
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const dialogWidth = dialogRef.current?.offsetWidth || 550;
        const dialogHeight = dialogRef.current?.offsetHeight || 400;

        let newX = e.clientX - dragStart.x;
        let newY = e.clientY - dragStart.y;

        // Boundaries
        const minX = -dialogWidth + 100;
        const maxX = window.innerWidth - 100;
        const minY = 0;
        const maxY = window.innerHeight - dialogHeight;

        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));

        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSave = () => {
    const unitsChanged =
      selectedLengthUnit !== lengthUnit || selectedForceUnit !== forceUnit;

    if (!unitsChanged) {
      onClose();
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmUpdate = () => {
    setLengthUnit(selectedLengthUnit);
    setForceUnit(selectedForceUnit);
    setShowConfirmation(false);
    onClose();

    setTimeout(() => {
      alert("✅ Units updated successfully!");
    }, 100);
  };

  const handleCancelUpdate = () => {
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setSelectedLengthUnit(lengthUnit);
    setSelectedForceUnit(forceUnit);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={handleCancel}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[550px] max-h-[85vh] overflow-hidden z-50"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Draggable Header */}
        <div
          ref={headerRef}
          className="bg-blue-600 text-white px-4 py-3 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex justify-between items-center"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <h2 className="text-lg font-semibold pointer-events-none">
            Unit Configuration
          </h2>
          <button
            onClick={handleCancel}
            className="text-white hover:text-gray-200 p-1 rounded transition-colors"
            title="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-120px)] p-6">
          <div className="grid grid-cols-2 gap-8">
            {/* Length Units Section */}
            <div className="border-r border-gray-200 dark:border-gray-700 pr-6">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4">
                Length Units
              </h3>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="inch"
                    checked={selectedLengthUnit === "inch"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Inch
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="foot"
                    checked={selectedLengthUnit === "foot"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Foot
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="millimeter"
                    checked={selectedLengthUnit === "millimeter"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Millimeter
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="centimeter"
                    checked={selectedLengthUnit === "centimeter"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Centimeter
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="decimeter"
                    checked={selectedLengthUnit === "decimeter"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Decimeter
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="meter"
                    checked={selectedLengthUnit === "meter"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Meter
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="lengthUnit"
                    value="kilometer"
                    checked={selectedLengthUnit === "kilometer"}
                    onChange={(e) =>
                      setSelectedLengthUnit(e.target.value as LengthUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Kilometer
                  </span>
                </label>
              </div>
            </div>

            {/* Force Units Section */}
            <div className="pl-2">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4">
                Force Units
              </h3>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="pound"
                    checked={selectedForceUnit === "pound"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Pound
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="kiloPound"
                    checked={selectedForceUnit === "kiloPound"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    KiloPound
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="kilogram"
                    checked={selectedForceUnit === "kilogram"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Kilogram
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="metricTon"
                    checked={selectedForceUnit === "metricTon"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Metric Ton
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="newton"
                    checked={selectedForceUnit === "newton"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    Newton
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="decaNewton"
                    checked={selectedForceUnit === "decaNewton"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    DecaNewton
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="kiloNewton"
                    checked={selectedForceUnit === "kiloNewton"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    KiloNewton
                  </span>
                </label>

                <label className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors">
                  <input
                    type="radio"
                    name="forceUnit"
                    value="megaNewton"
                    checked={selectedForceUnit === "megaNewton"}
                    onChange={(e) =>
                      setSelectedForceUnit(e.target.value as ForceUnit)
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    MegaNewton
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-600 px-6 py-3 flex justify-end gap-3 bg-gray-50 dark:bg-gray-750">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
          >
            OK
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[450px] z-[70] border border-gray-300 dark:border-gray-600">
            {/* Header */}
            <div className="bg-yellow-500 text-white px-4 py-3 rounded-t-lg flex items-center gap-3">
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="text-lg font-semibold">Confirm Unit Update</h3>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                This will update units <strong>everywhere</strong> in your
                project:
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Length:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {lengthUnit.charAt(0).toUpperCase() + lengthUnit.slice(1)}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400">→</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {selectedLengthUnit.charAt(0).toUpperCase() +
                        selectedLengthUnit.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Force:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {forceUnit === "kiloPound"
                        ? "KiloPound"
                        : forceUnit === "metricTon"
                          ? "Metric Ton"
                          : forceUnit === "decaNewton"
                            ? "DecaNewton"
                            : forceUnit === "kiloNewton"
                              ? "KiloNewton"
                              : forceUnit === "megaNewton"
                                ? "MegaNewton"
                                : forceUnit.charAt(0).toUpperCase() +
                                  forceUnit.slice(1)}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400">→</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {selectedForceUnit === "kiloPound"
                        ? "KiloPound"
                        : selectedForceUnit === "metricTon"
                          ? "Metric Ton"
                          : selectedForceUnit === "decaNewton"
                            ? "DecaNewton"
                            : selectedForceUnit === "kiloNewton"
                              ? "KiloNewton"
                              : selectedForceUnit === "megaNewton"
                                ? "MegaNewton"
                                : selectedForceUnit.charAt(0).toUpperCase() +
                                  selectedForceUnit.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to continue?
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-gray-600 px-6 py-3 flex justify-end gap-3 bg-gray-50 dark:bg-gray-750">
              <button
                onClick={handleCancelUpdate}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpdate}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
              >
                Yes, Update Units
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
